export interface AppConfigShape {
  cognito: {
    /** e.g. https://login.social-login.kai-sandbox.hopper.dev */
    hostedUiBaseUrl: string;
    /** e.g. https://login.social-login.kai-sandbox.hopper.dev/oauth2/token */
    tokenUrl: string;
    /** e.g. https://login.social-login.kai-sandbox.hopper.dev/oauth2/logout */
    logoutUrl: string;
    clientId: string;
    /** e.g. https://social-login.kai-sandbox.hopper.dev/callback */
    redirectUri: string;
    /** e.g. https://social-login.kai-sandbox.hopper.dev */
    postLogoutRedirectUri: string;
    scopes: string[];
  };
  api: {
    /** e.g. https://<apiId>.execute-api.<region>.amazonaws.com */
    baseUrl: string;
  };
}

export interface AppConfigEnv {
  VITE_COGNITO_HOSTED_UI_BASE_URL: string;
  VITE_COGNITO_CLIENT_ID: string;
  VITE_COGNITO_REDIRECT_URI: string;
  VITE_COGNITO_POST_LOGOUT_REDIRECT_URI: string;
  VITE_API_BASE_URL: string;
  VITE_COGNITO_SCOPES?: string;
}

let cached: AppConfigShape | null = null;

export class AppConfig {
  static get(): AppConfigShape {
    if (cached) return cached;

    const env = import.meta.env;

    const hostedUiBaseUrl = AppConfig.validateUrl(
      'VITE_COGNITO_HOSTED_UI_BASE_URL',
      env['VITE_COGNITO_HOSTED_UI_BASE_URL'] ?? '',
    );
    const clientId = env['VITE_COGNITO_CLIENT_ID'];
    if (!clientId) throw new Error('Missing required env var: VITE_COGNITO_CLIENT_ID');

    const redirectUri = AppConfig.validateUrl(
      'VITE_COGNITO_REDIRECT_URI',
      env['VITE_COGNITO_REDIRECT_URI'] ?? '',
    );
    const postLogoutRedirectUri = AppConfig.validateUrl(
      'VITE_COGNITO_POST_LOGOUT_REDIRECT_URI',
      env['VITE_COGNITO_POST_LOGOUT_REDIRECT_URI'] ?? '',
    );
    const apiBaseUrl = AppConfig.validateUrl(
      'VITE_API_BASE_URL',
      env['VITE_API_BASE_URL'] ?? '',
    );

    const scopesRaw = env['VITE_COGNITO_SCOPES'];
    const scopes = scopesRaw
      ? scopesRaw.trim().split(/\s+/)
      : ['openid', 'email', 'profile'];

    const base = hostedUiBaseUrl.replace(/\/$/, '');
    cached = {
      cognito: {
        hostedUiBaseUrl: base,
        tokenUrl: `${base}/oauth2/token`,
        // Cognito Hosted UI logout endpoint: /logout (NOT /oauth2/logout which returns 404)
        logoutUrl: `${base}/logout`,
        clientId,
        redirectUri,
        postLogoutRedirectUri,
        scopes,
      },
      api: {
        baseUrl: apiBaseUrl,
      },
    };
    return cached;
  }

  static validateUrl(name: string, value: string): string {
    if (!value || !value.trim()) {
      throw new Error(`Missing required env var: ${name}`);
    }
    try {
      const url = new URL(value.trim());
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        throw new Error(`Invalid protocol for ${name}: ${url.protocol}`);
      }
    } catch {
      throw new Error(`Invalid URL for ${name}: "${value}"`);
    }
    return value.trim();
  }

  /** Reset cache (useful in tests) */
  static _reset(): void {
    cached = null;
  }
}
